import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi } from "@/lib/api/bindery";
import { CostEstimateCard } from "./CostEstimateCard";
import { PaymentTimingCard } from "./PaymentTimingCard";
import { ThesisDetailsCard } from "./ThesisDetailsCard";
import { ESTIMATE_DEBOUNCE_MS } from "./newThesisConstants";
import { useThesisForm } from "./useThesisForm";

export default function NewThesisRequestPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const { form, isValid, setField, markTouched, markAllTouched, errorFor } = useThesisForm();
  const [estimating, setEstimating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState<{ unitPrice: number; totalPrice: number } | null>(null);

  const estimateInput = useMemo(
    () => ({
      degreeLevel: form.degreeLevel,
      bindingType: form.bindingType,
      pageCount: Number(form.pageCount) || 0,
      copies: Number(form.copies) || 1,
      colorPages: Number(form.colorPages) || 0,
    }),
    [form.degreeLevel, form.bindingType, form.pageCount, form.copies, form.colorPages]
  );

  useEffect(() => {
    let active = true;
    if (estimateInput.pageCount <= 0) {
      setEstimate(null);
      return;
    }
    const handle = setTimeout(async () => {
      setEstimating(true);
      try {
        const data = await binderyApi.thesis.estimate(estimateInput);
        if (active) {
          setEstimate({
            unitPrice: Number(data.unitPrice),
            totalPrice: Number(data.totalPrice),
          });
        }
      } catch {
        if (active) setEstimate(null);
      } finally {
        if (active) setEstimating(false);
      }
    }, ESTIMATE_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [estimateInput]);

  const submit = async () => {
    markAllTouched();
    if (!isValid) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await binderyApi.thesis.submit({
        degreeLevel: form.degreeLevel,
        title: form.title.trim(),
        supervisor: form.supervisor.trim(),
        pageCount: Number(form.pageCount),
        copies: Number(form.copies),
        bindingType: form.bindingType,
        colorPages: Number(form.colorPages) || 0,
        paperGrade: form.paperGrade.trim() || undefined,
        fileUrl: form.fileUrl.trim(),
        formattingNotes: form.formattingNotes.trim() || undefined,
      });
      toast.success("Request submitted");
      navigate(`/my/thesis/${res.id}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to submit request"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        title="New thesis request"
        description="Submit your thesis or dissertation for print and binding."
        breadcrumbItems={[
          { label: "My thesis requests", href: "/my/thesis" },
          { label: "New request" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <ThesisDetailsCard
            form={form}
            setField={setField}
            markTouched={markTouched}
            errorFor={errorFor}
          />
        </div>

        <div className="space-y-4">
          <CostEstimateCard
            estimating={estimating}
            estimate={estimate}
            copies={estimateInput.copies}
          />
          <PaymentTimingCard />
          <Button className="w-full" disabled={submitting || !isValid} onClick={submit}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Submit for review
          </Button>
          {!isValid && (
            <p className="text-center text-xs text-slate-500">
              Fill in all required fields to submit.
            </p>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
